import * as _errors from '@attain-apps/errors'
import type * as transformers from '@attain-apps/transformers'
import * as awsAmplifyApi from 'aws-amplify/api'
import * as awsAmplifyAuth from 'aws-amplify/auth'
import type * as graphql from 'graphql'
import * as rxjs from 'rxjs'

import { GraphqlResultError } from './graphql-result-error'
import { retryRequest } from './request-retry'
import type { OUT } from './transformers'

const client = awsAmplifyApi.generateClient()

// TODO: we should probably support passing in both input and output
// transformers so that we can use entity inputs rather than GraphQL inputs
/**
 * Executes a GraphQL operation with the current auth session and transforms the
 * response into the frontend entity shape expected by the caller.
 */
export function executeGql<
  TGqlResult,
  TReturn,
  TVariables extends object = never,
>({
  variables,
  query,
  transformer,
  retry,
}: executeGql.Params<
  TGqlResult,
  TReturn,
  TVariables
>): rxjs.Observable<TReturn> {
  const operationName = _getOperationName(query)
  const operationType = _getOperationType(query)

  const request$ = rxjs
    .defer(async () => {
      // Keep auth lookup and GraphQL execution cold so every subscription and
      // retry gets a fresh session and sends a new network request.
      const session = await awsAmplifyAuth.fetchAuthSession()
      const authToken = session.tokens?.accessToken.toString()
      if (!authToken) {
        throw new Error('Unable to get current user session')
      }
      return client.graphql<awsAmplifyApi.GraphQLQuery<unknown>>({
        authToken,
        // NOTE: Amplify does not use the same version of GraphQL as us, so we need
        // to cast to their version
        query: query satisfies string | graphql.DocumentNode as
          | string
          | import('../../../../../../../../../node_modules/.pnpm/graphql@15.8.0/node_modules/graphql/language/ast').DocumentNode,
        variables,
        // NOTE: typescript defers the evaluation of type parameters which is why we need to cast here
      }) satisfies Promise<awsAmplifyApi.GraphQLResult<unknown>> as Promise<
        awsAmplifyApi.GraphQLResult<TGqlResult>
      >
    })
    .pipe(
      // NOTE: weirdly, AppSync throws errors in the same format as the result,
      // according to:
      // https://github.com/aws-amplify/amplify-js/issues/10574#issuecomment-1300877766
      rxjs.catchError((result: unknown) => {
        if (result instanceof Error) {
          // If we do end up getting an error, we throw it
          return rxjs.throwError(() => result)
        }
        // Otherwise, we just process the result as usual
        return rxjs.of(result as awsAmplifyApi.GraphQLResult<TGqlResult>)
      }),
      rxjs.map(
        ({ data, errors = [] }: awsAmplifyApi.GraphQLResult<TGqlResult>) => {
          if (errors.length) {
            throw new GraphqlResultError(errors)
          }
          // NOTE: Transformers can decide how they want to handle undefined data
          return transformer.getEntityFrom(
            data satisfies TGqlResult | undefined,
          )
        },
      ),
    )

  return retryRequest(request$, {
    operationName,
    operationType,
    retry,
  }).pipe(
    rxjs.catchError(error => {
      // We log the error to make debugging easier
      console.warn(
        `Failed to execute: "${
          typeof query === 'string'
            ? query
            : `${operationType} ${operationName}`
        }"`,
        error,
      )
      // And make them user-friendly
      return rxjs.throwError(() => _errors.makeUserFriendly(error))
    }),
  )
}
export namespace executeGql {
  export interface Params<
    TGqlResult,
    TReturn,
    TVariables extends object = never,
  > {
    query: string | graphql.DocumentNode
    transformer: transformers.Transformer<
      transformers.Factory.OutputType<typeof OUT>,
      TGqlResult,
      TReturn
    >
    /**
     * Controls lightweight request retry for this operation.
     *
     * Queries retry by default. Mutations retry when a retry config object is
     * provided.
     */
    retry?: retryRequest.Config
    variables?: TVariables
  }
}

/**
 * Creates a function that can be used to execute the GraphQL operation given to
 * this function
 */
export function createExecutor<
  TGqlResult,
  TReturn,
  TVariables extends object = never,
>(
  params: Readonly.Deep<createExecutor.Params<TGqlResult, TReturn>>,
): Partial<TVariables> extends TVariables
  ? // If all of the variables are optional, then we can make variables as a whole
    // optional
    (variables?: TVariables) => rxjs.Observable<TReturn>
  : // Otherwise, we must require variables
    (variables: TVariables) => rxjs.Observable<TReturn>
export function createExecutor<
  TGqlResult,
  TReturn,
  TVariables extends object = never,
>({
  query,
  retry,
  transformer,
}: Readonly.Deep<createExecutor.Params<TGqlResult, TReturn>>) {
  return (variables?: TVariables) =>
    executeGql({ query, retry, transformer, variables })
}
export namespace createExecutor {
  export type Params<TGqlResult, TReturn> = Omit<
    executeGql.Params<TGqlResult, TReturn>,
    'variables'
  >
}

/** Gets the operation name for logging failed GraphQL executions. */
function _getOperationName(
  query: string | Readonly.Deep<graphql.DocumentNode>,
): string | undefined {
  if (typeof query === 'string') {
    return /^\s*(?:query|mutation|subscription)\s+([_A-Za-z][_0-9A-Za-z]*)/.exec(
      query,
    )?.[1]
  }

  return (
    query.definitions.find(
      it => it.kind === 'OperationDefinition',
    ) as graphql.OperationDefinitionNode
  )?.name?.value
}

/** Gets the operation type for logging failed GraphQL executions. */
function _getOperationType(
  query: string | Readonly.Deep<graphql.DocumentNode>,
): graphql.OperationTypeNode | undefined {
  if (typeof query === 'string') {
    const operationType = /^\s*(query|mutation|subscription)\b/.exec(
      query,
    )?.[1] as graphql.OperationTypeNode | undefined
    return (
      operationType ??
      (/^\s*\{/.test(query)
        ? ('query' as graphql.OperationTypeNode)
        : undefined)
    )
  }

  return (
    query.definitions.find(
      it => it.kind === 'OperationDefinition',
    ) as graphql.OperationDefinitionNode
  ).operation
}
